import React from 'react';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import ResellerDashboard from '../ResellerDashboard';
import { deletePlan, getAllResellerPlans, unArchive } from '@/api/reseller';
import { Card, Row, Col, Typography, Button, Space, notification } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import {
	ClockCircleOutlined,
	SettingOutlined,
	DeleteOutlined,
	PhoneOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import FeatureGatingCustomizeModal from '../Reseller_Common_Components/FeatureGatingCustomizeModal';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import { CustomModal } from '@/components/CustomModal';
import { useNavigate } from 'react-router-dom';
import SMSPricingModal from '@/components/SMSPricingModal';
import { currencyOptions } from 'src/constants/unitPrice';

const { Title, Text } = Typography;

export default function ResellerPlanManagement() {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();

	const [features, setFeatures] = React.useState<Record<string, any> | undefined>(undefined);
	const [deleteModalVisible, setDeleteModalVisible] = React.useState(false);
	const [selectedPlan, setSelectedPlan] = React.useState<any>(null);
	const resellerData = useAppSelector(getResellerData);

	const navigate = useNavigate();

	// Fetch all plans
	const { data: plans, isLoading } = useQuery({
		queryKey: ['getAllResellerPlans'],
		queryFn: () =>
			getAllResellerPlans({
				resellerId: resellerData?.id as string,
			}),
		enabled: !!resellerData?.stripe_connect_id,
	});
	// Delete plan mutation
	const deletePlanMutation = useMutation({
		mutationFn: ({ planId, connectId }: { planId: string; connectId: string }) =>
			deletePlan({ planId, connectId }),
		onSuccess: () => {
			notification.success({
				message: 'Plan archived successfully',
				description:
					'Existing subscribers can continue using it, but new subscriptions are disabled.',
			});
			queryClient.invalidateQueries({ queryKey: ['getAllResellerPlans'] });
			setDeleteModalVisible(false);
			setSelectedPlan(null);
		},
		onError: (error: any) => {
			notification.error({
				message: 'Failed to archive plan',
				description: error.message || 'An error occurred while archiving the plan',
			});
		},
	});

	const unArchivePlanMutation = useMutation({
		mutationFn: ({ planId, connectId }: { planId: string; connectId: string }) =>
			unArchive({ planId, connectId }),
		onSuccess: () => {
			notification.success({
				message: 'Plan unarchived successfully',
				description:
					'Existing subscribers can continue using it, and new subscriptions are enabled.',
			});
			queryClient.invalidateQueries({ queryKey: ['getAllResellerPlans'] });
		},
		onError: (error: any) => {
			notification.error({
				message: 'Failed to unarchive plan',
				description: error.message || 'An error occurred while unarchiving the plan',
			});
		},
	});

	const handleFeatureClick = (featuresSubAccount: any) => {
		dispatch(
			openModal({
				modalId: MODAL_IDS.FEATURE_GATING_CUSTOMIZE_MODAL,
				data: {
					isTrialForm: false,
					planCreation: true,
					viewOnly: true,
					featuresSubAccount,
				},
			}),
		);
	};

	const handleDeleteClick = (plan: any) => {
		setSelectedPlan(plan);
		if (plan.active === false) {
			if (resellerData?.stripe_connect_id) {
				unArchivePlanMutation.mutate({
					planId: plan.id,
					connectId: resellerData?.stripe_connect_id,
				});
			} else {
				notification.error({
					message: 'Failed to unarchive plan',
					description: 'Stripe connect ID is missing',
				});
			}
			return;
		}
		setDeleteModalVisible(true);
	};

	const handleDeleteConfirm = () => {
		if (selectedPlan) {
			if (resellerData?.stripe_connect_id) {
				deletePlanMutation.mutate({
					planId: selectedPlan.id,
					connectId: resellerData?.stripe_connect_id,
				});
			} else {
				notification.error({
					message: 'Failed to archive plan',
					description: 'Stripe connect ID is missing',
				});
			}
		}
	};

	const getProviderName = (key: string): string => {
		const providers: Record<string, string> = {
			deepgram: 'Deepgram Voice',
			cartesia: 'Cartesia Voice',
			'11labs': '11Labs Voice',
			transferCall: 'Transfer call',
		};
		return providers[key] || key;
	};

	const renderVoiceRates = (meterPrices: Record<string, number>) => {
		const voiceRates: Record<string, number> = {};

		// Collect rates for voice providers (excluding transferCall)
		Object.entries(meterPrices).forEach(([key, value]) => {
			if (key !== 'transferCall') {
				voiceRates[key] = value;
			}
		});

		// Check if all voice rates are the same
		const rates = Object.values(voiceRates);
		const allRatesSame = rates.length > 0 && rates.every((rate) => rate === rates[0]);

		return (
			<Space direction="vertical" className="w-full">
				{allRatesSame ? (
					<div className="flex justify-between items-center">
						<Text style={{ color: '#6B7280' }}>
							<PhoneOutlined className="mr-2" />
							Call per minute
						</Text>
						<Text strong style={{ color: '#111827' }}>
							{rates[0] ? `$${rates[0]}/min` : 'Free'}
						</Text>
					</div>
				) : (
					Object.entries(meterPrices).map(([key, value]) => {
						if (key === 'transferCall') {
							return null;
						}
						return (
							<div key={key} className="flex justify-between items-center">
								<Text style={{ color: '#6B7280' }}>
									<PhoneOutlined className="mr-2" />
									{getProviderName(key)}
								</Text>
								<Text strong style={{ color: '#111827' }}>
									{value ? `$${value}/min` : 'Free'}
								</Text>
							</div>
						);
					})
				)}
			</Space>
		);
	};

	return (
		<ResellerDashboard activeKey={ResellerSiderItem.PLAN_VIEW} isPageLoading={isLoading}>
			<FeatureGatingCustomizeModal
				planFeatures={features}
				setPlanFeatures={(featuresObject: Record<string, any>) => setFeatures(featuresObject)}
			/>
			<div className="p-6 bg-gray-50 min-h-screen">
				<div className="max-w-7xl mx-auto">
					<div className="flex justify-between items-center mb-6">
						<Title level={2}>Your Plans</Title>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => navigate('/reseller_portal/plan-creation')}
						>
							Create New Plan
						</Button>
					</div>

					{!plans?.plans?.length ? (
						<div className="flex flex-col items-center justify-center min-h-[400px] bg-white rounded-lg shadow-sm p-8">
							<div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									className="h-6 w-6 text-blue-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M12 4v16m8-8H4"
									/>
								</svg>
							</div>
							<Title level={3} className="mb-2">
								Create Your First Plan
							</Title>
							<Text className="text-gray-500 text-center mb-6">
								Start by creating a subscription plan for your customers.
								<br />
								You can set pricing, roles, and included features.
							</Text>
							<Button
								type="primary"
								size="large"
								onClick={() => navigate('/reseller_portal/plan-creation')}
								className="px-8"
							>
								Create New Plan
							</Button>
						</div>
					) : (
						<Row gutter={[24, 24]}>
							{plans.plans.map((plan: any) => (
								<Col xs={24} sm={12} md={8} key={plan.id}>
									<Card
										className="h-full relative overflow-hidden"
										bodyStyle={{
											padding: '24px',
											height: '100%',
											display: 'flex',
											flexDirection: 'column',
										}}
										bordered={false}
									>
										{plan.name.toLowerCase().includes('pro') && (
											<div className="absolute top-0 left-0 w-full bg-blue-500 text-white text-center py-1 text-sm">
												Popular
											</div>
										)}
										<div className="flex flex-col h-full">
											<div className="flex-grow">
												<div className="mb-6">
													<Text className="text-4xl font-semibold">
														{currencyOptions.find((opt) => opt.value === plan.currency)?.symbol ||
															'$'}
														{plan.monthly_price}
													</Text>
													<Text className="text-gray-500">/month</Text>
												</div>

												<div className="mb-6">
													<div className="flex items-center gap-2 text-gray-600 mb-2">
														<ClockCircleOutlined />
														<Text>Free Credits</Text>
													</div>
													<Text className="text-xl block">
														{plan.free_minutes} <span className="text-gray-500">credits/month</span>
													</Text>
												</div>
												<div className="mb-6">
													<div className="flex items-center gap-2 text-gray-600 mb-2">
														<SmsOutlinedIcon style={{ fontSize: '15px' }} />
														<Text>Free SMS</Text>
													</div>
													<Text className="text-xl block">
														{plan.freeSMSCredits}{' '}
														<span className="text-gray-500">credits/month</span>
													</Text>
												</div>
												<div>
													{plan.trialDays > 0 && (
														<div className="flex flex-col gap-2 my-4">
															<Text className="text-xl font-semibold">Trial Included</Text>
															<Text className="text-xl block">{plan.trialDays} days</Text>
															{plan.trialMinutes > 0 && (
																<Text className="text-gray-500 text-md font-semibold">
																	{plan.trialMinutes} minutes
																</Text>
															)}
														</div>
													)}
												</div>

												<div className="space-y-3">
													<div className="flex items-center gap-2 text-gray-600">
														<PhoneOutlined />
														<Text>Voice Rates</Text>
													</div>
													{renderVoiceRates(plan.meterPrices)}
													<div
														style={{
															display: 'flex',
															justifyContent: 'space-between',
															alignItems: 'center',
															marginBottom: '8px',
														}}
													>
														<Text style={{ color: '#6B7280' }}>
															{' '}
															<SmsOutlinedIcon className="mr-2" style={{ fontSize: '15px' }} /> SMS
															Pricing
														</Text>
														<Button
															type="link"
															style={{ color: '#6366f1' }}
															className="font-medium px-0"
															onClick={() => {
																dispatch(
																	openModal({
																		modalId: MODAL_IDS.SMS_PRICING,
																		data: { planId: plan.id },
																	}),
																);
															}}
														>
															See Pricing
														</Button>
													</div>
												</div>
											</div>

											<div className="flex justify-between items-center pt-4 mt-6 border-t border-gray-100">
												<Button
													type="text"
													className="flex items-center text-gray-600"
													onClick={() => handleFeatureClick(plan.featuresSubAccount)}
												>
													<SettingOutlined className="mr-2" />
													Features
												</Button>
												<Button
													type="text"
													className="flex items-center text-red-500"
													onClick={() => handleDeleteClick(plan)}
												>
													<DeleteOutlined className="mr-2" />
													{plan.active === false ? 'Unarchive' : 'Archive'}
												</Button>
											</div>
										</div>
									</Card>
								</Col>
							))}
						</Row>
					)}
				</div>
				<SMSPricingModal />
				<CustomModal
					title="Archive Plan"
					isModalOpen={deleteModalVisible}
					confirmAction={handleDeleteConfirm}
					cancelAction={() => {
						setDeleteModalVisible(false);
						setSelectedPlan(null);
					}}
					loading={deletePlanMutation.isPending}
					okText="Archive"
				>
					<Title level={4}>Are you sure you want to archive this plan?</Title>
					<p>
						Existing subscribers will continue to have access, but new subscriptions will be
						disabled.
					</p>
					{selectedPlan && (
						<div className="mt-4 p-4 bg-gray-50 rounded">
							<Text strong>Plan Details:</Text>
							<div className="mt-2">
								<div>Name: {selectedPlan.name}</div>
								<div>Price: ${selectedPlan.monthly_price}/month</div>
								<div>Free Minutes: {selectedPlan.free_minutes}</div>
							</div>
						</div>
					)}
				</CustomModal>
			</div>
		</ResellerDashboard>
	);
}
